import {BaseGraphNode} from "#graph/nodes/base/node"
import {CircularRef} from "#graph/nodes/circular-ref/node"
import {GraphNodeDifferences} from "#graph/diffing/graph-node-differences"
import {GraphNodeSnapshot} from "#graph/diffing/graph-node-snapshot"
import {hashObject} from "@cm/utils"
import type {Evaluator} from "#graph/evaluators/types"
import {ensureValidParameters, mapGraphParameters, traverseGraphParameters} from "#graph/helpers/parameters"
import {getGraphNodesByInstanceIds} from "#graph/helpers/collect"
import {graphParametersDifferences} from "#graph/helpers/diffing"
import {recursivelyReplaceSubObjects} from "#graph/helpers/replace"
import type {BaseGraphNodeDefinition, GetInputParameters, GetParameters, NodeParameters} from "#graph/nodes/base/types"
import type {GetReturns, GraphNodeDefinition} from "#graph/nodes/graph/types"
import {serializeGraphNode} from "#graph/serialization/serialize"
import {jsonSchema} from "#graph/helpers/json"
import type {ParameterDifferences} from "#graph/diffing/types"
import type {SerializedGraphNode} from "#graph/serialization/types"
import {deserializeGraphNode} from "#graph/serialization/deserialize"

export abstract class GraphNode<out Definition extends GraphNodeDefinition = GraphNodeDefinition, in Context = any> extends BaseGraphNode<Definition> {
    public children: Set<GraphNode> = new Set()
    public parents: Set<GraphNode> = new Set()

    public constructor(parameters: GetInputParameters<Definition>, disableValidation = false) {
        super(parameters, disableValidation)

        this.parameters = traverseGraphParameters<BaseGraphNode>(
            this.parameters,
            (value) => value instanceof GraphNode,
            (node) => {
                if (node instanceof GraphNode) {
                    this.children.add(node)
                    node.parents.add(this)
                }
            },
        )
    }

    run = (_evaluator: Evaluator<Context>): Promise<GetReturns<Definition>> => {
        throw new Error(`Method run is not implemented for ${this}`)
    }
    runSync = (_evaluator: Evaluator<Context>): GetReturns<Definition> => {
        throw new Error(`Method runSync is not implemented for ${this}`)
    }

    override replaceParameters(newParameters: GetInputParameters<Definition>, disableValidation = false) {
        const parameters = !disableValidation ? ensureValidParameters(this, newParameters) : newParameters
        traverseGraphParameters(
            this.parameters,
            (value) => value instanceof BaseGraphNode,
            (node) => {
                if (node instanceof GraphNode) {
                    this.children.delete(node)
                    node.parents.delete(this)
                }
            },
        )
        this.parameters = traverseGraphParameters(
            parameters,
            (value) => value instanceof BaseGraphNode,
            (node) => {
                if (node instanceof GraphNode) {
                    this.children.add(node)
                    node.parents.add(this)
                }
            },
        )
    }

    // returning = (schema: z.ZodSchema) => {
    //     // horrible hack: isCompatibleType does not work well with any or unknown,
    //     // so we need this special case
    //     const returnsDef = this.definition.validation.returns._def
    //     if ("typeName" in returnsDef && returnsDef["typeName"] === "ZodAny") {
    //         return true
    //     }
    //     return isCompatibleType(this.definition.validation.returns, schema)
    // }

    getReturnSchema() {
        return this.definition.validation.returns
    }

    serialize() {
        const serializedGraph = serializeGraphNode(this)
        return jsonSchema.parse(serializedGraph) as SerializedGraphNode
    }

    //For illustration of orders, see: https://en.wikipedia.org/wiki/Tree_traversal
    private _iterate = (depthFirst: boolean = false): Iterator<GraphNode> => {
        const visitedNodes = new Set<GraphNode>()
        let nodesToVisit: GraphNode[] = [this]
        const visitNode = () => {
            const firstNode = nodesToVisit.pop()
            if (firstNode) {
                visitedNodes.add(firstNode)
                nodesToVisit = (depthFirst ? [...firstNode.children, ...nodesToVisit] : [...nodesToVisit, ...firstNode.children]).filter(
                    (node) => !visitedNodes.has(node),
                )
            }
            return firstNode
        }
        return {
            next: () => {
                const node = visitNode()
                if (node) {
                    return {value: node, done: false}
                } else {
                    return {value: undefined, done: true}
                }
            },
        }
    }

    public [Symbol.iterator] = () => this._iterate(true)

    clone(options?: {
        cloneSubNode?: (parent: BaseGraphNode, subNode: BaseGraphNode, path: string) => boolean
        parameterOverrides?: Partial<GetParameters<Definition>> | ((node: BaseGraphNode) => NodeParameters | undefined)
        cloneInstanceIds?: boolean
    }): this {
        const cloneSubNodes = options?.cloneSubNode ?? (() => false)
        const parameterOverrides = options?.parameterOverrides
        const parameterOverridesFn =
            typeof parameterOverrides === "function"
                ? parameterOverrides
                : (node: BaseGraphNode) => {
                      if (node === this) return parameterOverrides
                      return undefined
                  }
        const cloneInstanceIds = options?.cloneInstanceIds ?? false

        const replacedNodes = new Map<BaseGraphNode, BaseGraphNode>()
        const clonedChildPaths = new Map<BaseGraphNode, Set<string>>()
        function cloneImpl(node: BaseGraphNode): BaseGraphNode {
            const cachedRefId = replacedNodes.get(node)
            if (cachedRefId !== undefined) return cachedRefId

            const cycleRef = new CircularRef({refId: replacedNodes.size})
            replacedNodes.set(node, cycleRef)

            const childPaths = new Set<string>()
            const parameterOverrides = parameterOverridesFn(node)
            const retVal = new (node.constructor as {
                new (parameters: GetInputParameters<BaseGraphNodeDefinition>, disableValidation: boolean): BaseGraphNode
            })(
                {
                    ...traverseGraphParameters<BaseGraphNode>(
                        node.parameters,
                        (value) => value instanceof BaseGraphNode,
                        (child, path) => {
                            if ((!parameterOverrides || !(path in parameterOverrides)) && cloneSubNodes(node, child, path)) {
                                childPaths.add(path)
                                return cloneImpl(child)
                            } else return child
                        },
                    ),
                    ...parameterOverrides,
                },
                true,
            )
            if (cloneInstanceIds) retVal.instanceId = node.instanceId

            replacedNodes.set(node, retVal)
            replacedNodes.set(cycleRef, retVal)
            clonedChildPaths.set(retVal, childPaths)

            return retVal
        }

        const clonedNode = cloneImpl(this) as this

        const visitedNodes = new Set<BaseGraphNode>()
        function replaceClonedSubNodes(node: BaseGraphNode) {
            const childPaths = clonedChildPaths.get(node)
            if (!childPaths) return

            const newParameters = traverseGraphParameters<BaseGraphNode>(
                node.parameters,
                (value) => value instanceof BaseGraphNode,
                (child, path) => {
                    if (!childPaths.has(path)) return child

                    const retValue = replacedNodes.get(child) ?? child

                    if (visitedNodes.has(retValue)) return retValue
                    visitedNodes.add(retValue)

                    replaceClonedSubNodes(retValue)
                    return retValue
                },
            )
            node.replaceParameters(newParameters)
        }
        replaceClonedSubNodes(clonedNode)

        return clonedNode
    }

    getSnapshot(): GraphNodeSnapshot<BaseGraphNode, GraphNode> {
        return new GraphNodeSnapshot<BaseGraphNode, GraphNode>(this)
    }

    trackDifferences(modify: (graph: this) => void, keepChanges = true): GraphNodeDifferences<GraphNode> {
        const snapshot = this.getSnapshot()
        modify(this)
        const differences = this.getDifferences(snapshot)
        if (!keepChanges) differences.applyBackward()
        return differences
    }

    getDifferences(oldSnapshot: GraphNodeSnapshot<BaseGraphNode, GraphNode>): GraphNodeDifferences<GraphNode> {
        const {originalNodes, oldClonedNodes, node} = oldSnapshot
        if (node !== this) throw new Error("NodeGraphSnapshot does not match this node")

        const newNodes = getGraphNodesByInstanceIds<GraphNode>(this)

        const allNodes = new Map<string, GraphNode>([...originalNodes, ...newNodes])

        const addedNodes = new Set<GraphNode>()
        const modifiedNodes = new Map<GraphNode, ParameterDifferences>()
        const deletedNodes = new Set<GraphNode>()
        for (const newNode of allNodes.values()) {
            if (!newNodes.has(newNode.instanceId)) deletedNodes.add(newNode)

            const preChangeNodeClone = oldClonedNodes.get(newNode.instanceId)
            if (!preChangeNodeClone) {
                addedNodes.add(newNode)
            } else {
                const preChangedNodeParameters = traverseGraphParameters<BaseGraphNode>(
                    preChangeNodeClone.parameters,
                    (value) => value instanceof BaseGraphNode,
                    (node) => {
                        const originalNode = originalNodes.get(node.instanceId)
                        if (!originalNode) throw new Error(`Original node ${node.instanceId} not found in graph`)
                        return originalNode
                    },
                )
                const modifiedParameters = graphParametersDifferences(preChangedNodeParameters, newNode.parameters, true)
                if (modifiedParameters.length > 0) modifiedNodes.set(newNode, modifiedParameters)
            }
        }

        return new GraphNodeDifferences<GraphNode>(this, addedNodes, modifiedNodes, deletedNodes)
    }

    getHash() {
        type HashableObject = {
            $class: string
            $parameters: NodeParameters
            $version: number
        }

        const resolvedHashableObjects = new Map<CircularRef, HashableObject>()

        const getHashableObject = (node: BaseGraphNode, refCache: Map<BaseGraphNode, HashableObject | CircularRef>): HashableObject | CircularRef => {
            const cachedHashableObject = refCache.get(node)
            if (cachedHashableObject) return cachedHashableObject

            const circularRef = new CircularRef({refId: refCache.size})
            refCache.set(node, circularRef)

            const hashableObject: HashableObject = {
                $class: node.nodeClass,
                $parameters: mapGraphParameters<BaseGraphNode, CircularRef | HashableObject>(
                    node.parameters,
                    (value) => value instanceof BaseGraphNode,
                    (child) => getHashableObject(child, refCache),
                ),
                $version: node.nodeVersion,
            }

            refCache.set(node, hashableObject)
            resolvedHashableObjects.set(circularRef, hashableObject)

            return hashableObject
        }

        const hashableObject = getHashableObject(this, new Map()) as HashableObject

        const objectMapper = (object: object): object | void => {
            if (object instanceof CircularRef) {
                const resolvedHashObject = resolvedHashableObjects.get(object)
                if (!resolvedHashObject) {
                    throw new Error(`Circular reference ${object.parameters.refId} not found in resolved hash objects`)
                }
                return resolvedHashObject
            }
        }

        recursivelyReplaceSubObjects(hashableObject, objectMapper)
        return hashObject(hashableObject)
    }

    public static deserialize<OwnType, Parameters extends NodeParameters = NodeParameters>(
        this: {new (parameters: Parameters, disableValidation?: boolean): OwnType},
        data: SerializedGraphNode,
    ): OwnType {
        const result = deserializeGraphNode(data)
        if (result instanceof this) {
            return result
        }
        // nodeClass is a static property, but we cannot declare it as both static and abstract, so this cast is needed
        throw new Error(`Cannot deserialize node of class ${result.nodeClass} as ${(this as unknown as {nodeClass: string}).nodeClass}`)
    }
}
